import React from 'react';
import clsx from 'clsx';
import IconButton from '@material-ui/core/IconButton';

import './SearchTextField.css';
import { noop } from 'util/function';
import MaterialIcon from 'components/icon/MaterialIcon';
import TextField, { TextFieldProps } from 'components/input/TextField';

//
// --- Types ---

export type SearchTextFieldProps = TextFieldProps & {
  afterTextInputContent?: React.ReactNode;
  className?: string;
  onSearchSubmit?: (searchPhrase: string) => void;
  afterSearchIconContent?: React.ReactNode;
  useNewSupplierSearch: boolean;
};

//
// --- SearchTextField Component ---

/**
 * SearchTextField combines a TextField and IconButton component into a single component and
 * handles "search submission" if a user hits the enter key or clicks to search icon button.
 */
const SearchTextField: React.FC<SearchTextFieldProps> = props => {
  const {
    afterTextInputContent,
    className,
    onKeyDown = noop,
    onSearchSubmit = noop,
    afterSearchIconContent,
    useNewSupplierSearch,
    ...remainingTextFieldProps
  } = props;
  const textFieldRef = React.useRef<HTMLInputElement>(null);

  const submitSupplierSearch = () => {
    const searchPhrase = textFieldRef.current?.value.trim() || '';
    onSearchSubmit(searchPhrase);
  };

  const handleTextFieldKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown(event); // pass event to parent as well

    if (event.key === 'Enter') {
      submitSupplierSearch();
    }
  };

  return (
    <div className={clsx('search-textfield', className)} data-testid="search-textfield">
      <TextField
        className={clsx('search-textfield-input', className)}
        inputRef={textFieldRef}
        onKeyDown={handleTextFieldKeyDown}
        {...remainingTextFieldProps}
      />
      {afterTextInputContent && (
        <div className="search-textfield-after-input-content-container">{afterTextInputContent}</div>
      )}

      <IconButton
        aria-label="search suppliers"
        className="search-textfield-button"
        color="primary"
        onClick={submitSupplierSearch}
      >
        <MaterialIcon className="search-textfield-icon" name="search" />
      </IconButton>

      {afterSearchIconContent && <div>{afterSearchIconContent}</div>}
    </div>
  );
};

export default SearchTextField;
